import React from 'react';
import usePreflightTest from './usePreflightTest/usePreflightTest';
import { makeStyles, Typography, Grid } from '@material-ui/core';
import useGetPreflightTokens from './useGetPreflightTokens/useGetPreflightTokens';
import getNetworkCondition from './getNetworkCondition/getNetworkCondition';
import ProgressIndicator from './ProgressIndicator/ProgressIndicator';
import { SuccessIcon } from '../../../icons/SuccessIcon';
import WarningIcon from '../../../icons/WarningIcon';
import ErrorIcon from '../../../icons/ErrorIcon';
import { useTranslation } from 'react-i18next';

export const TEST_DURATION = 10000;

export enum NetworkCondition {
  Red,
  Yellow,
  Green,
}

const useStyles = makeStyles({
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '0.4em',
  },
  result: {
    color: 'white',
  },
});

function ResultItem({ children, icon }: { children: React.ReactNode; icon: React.ReactNode }) {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <>
      <div className={classes.iconContainer}>{icon}</div>
      <Typography variant="subtitle2" className={classes.result}>
        {children}
      </Typography>
    </>
  );
}

export function Result(
  { t }: any,
  { networkCondition, error }: { networkCondition?: NetworkCondition; error?: Error }
) {
  if (error) {
    return <ResultItem icon={<ErrorIcon />}>{t('common.error-connexion-text')}</ResultItem>;
  }

  if (networkCondition === NetworkCondition.Red) {
    return <ResultItem icon={<ErrorIcon />}>{t('bad-quality-1-connexion-text')}</ResultItem>;
  }

  if (networkCondition === NetworkCondition.Yellow) {
    return <ResultItem icon={<WarningIcon />}>{t('bad-quality-2-connexion-text')}</ResultItem>;
  }

  if (networkCondition === NetworkCondition.Green) {
    return <ResultItem icon={<SuccessIcon />}>{t('common.stable-connexion-text')}</ResultItem>;
  }

  return null;
}

export default function PreflightTest() {
  const classes = useStyles();

  const { tokens, tokenError } = useGetPreflightTokens();
  const { testFailure, testReport } = usePreflightTest(tokens?.[0], tokens?.[1]);

  const networkCondition = getNetworkCondition(testReport);

  const { t } = useTranslation();

  return (
    <Grid container justify="center" alignItems="center" style={{ height: '40px', paddingBottom: '1.5em' }}>
      {!testFailure && !testReport ? (
        <>
          <ProgressIndicator />
          <Typography variant="subtitle2" className={classes.result}>
            {t('common.checking-connexion-text')}
          </Typography>
        </>
      ) : (
        <Result networkCondition={networkCondition} error={tokenError || testFailure} />
      )}
    </Grid>
  );
}
